import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { AiInsightsRootModalScreenParamList } from "../../../../types";
import {
  Page,
  useFlashMessages,
  View,
  Text,
  Button,
} from "@towersystems/roam-common-native";
import { ScreenContext } from "./common";
import React from "react";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import { ExecuteLlmQuickWinResult } from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";
import { WebView } from "react-native-webview";
import { formatDateToCalendar } from "@towersystems/roam-common/lib/shared-utils";

export type GenerateIconScreenProps = NativeStackScreenProps<
  AiInsightsRootModalScreenParamList,
  "AiInsightsScreen"
>;

export const AiInsightsScreen = ({ navigation }: GenerateIconScreenProps) => {
  const [result, setResult] = React.useState<
    | {
        result?: string;
        id: string;
      }
    | undefined
  >(undefined);

  const transitionEnded = useHasTransitionEnded(navigation);

  const screenContext = React.useContext(ScreenContext);
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={"Actions"}
          isAction
          disabled={!result?.id}
          onPress={() => {
            navigation.navigate("AiInsightsActionsRootScreen", {
              screen: "AiInsightsActionsListScreen",
              resultId: result?.id || "",
            });
          }}
          size="compact"
        ></Button>
      ),
    });
  }, [result?.id]);

  React.useEffect(() => {
    (async () => {
      return handleOnGenerate();
    })();
  }, []);

  const handleOnGenerate = async () => {
    if (!screenContext) return;

    setResult(undefined);
    return m({
      variables: {
        input: {
          sales_period_summary_quick_win: {
            startDateTime: screenContext.startDate.toISOString(),
            endDateTime: screenContext.endDate
              ? screenContext.endDate.toISOString()
              : undefined,
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          setResult({ result: result.result, id: result.id });
        } else {
          if (result && "message" in result) {
            throw new Error(result.message);
          }
        }
      })
      .catch((error) => {
        flashMessagesContext.showMessage({
          message: "Failed to generate description. Check your credentials.",
          severity: "error",
        });
      });
  };

  if (result === undefined || !transitionEnded || !screenContext) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const htmlContent = `
<!DOCTYPE html>
<html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        padding: 0;
        font-size: 14px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      }
    </style>
  </head>
  <body>
    ${result?.result || ""}
  </body>
</html>
`;

  return (
    <Page scrollable={false}>
      <Text
        variant="title"
        fontWeight="bold"
        numberOfLines={2}
        ellipsizeMode="head"
      >
        AI Insights
      </Text>
      <View style={{ marginBottom: 20 }}>
        <Text variant="heading2" color="subdued">
          {`Insights for period: ${formatDateToCalendar(
            screenContext.startDate.toISOString(),
            "ddd MMM D YYYY",
          )} - ${
            screenContext.endDate
              ? formatDateToCalendar(
                  screenContext.endDate.toISOString(),
                  "ddd MMM D YYYY",
                )
              : "Present"
          }`}
        </Text>
      </View>
      <WebView
        originWhitelist={["*"]}
        source={{ html: htmlContent }}
        style={{ flex: 1 }}
      />
    </Page>
  );
};
